import {
  Clock,
  CalendarDays,
  KanbanSquare,
  Rocket,
  ListChecks,
  Table2,
  Receipt,
  UserPlus,
  UserMinus,
  Briefcase,
  Gift,
  Bug,
  Laptop,
  Trophy,
  BarChart3,
  Megaphone,
  Timer,
  ShieldCheck,
  Wallet,
  Palette,
} from "lucide-react";

// The 20 Velacin modules — single source of truth for Home showcase + Features + SEO.
export const MODULES = [
  {
    index: "01",
    slug: "attendance",
    icon: Clock,
    title: "Attendance & Break Tracking",
    tagline: "Live timers. Explicit breaks. Zero disputes.",
    description:
      "Multi-session daily attendance with a live HH:MM:SS timer, explicit break start/end, and total work / break minute reconciliation. Guard-rails prevent double check-in or check-out while on break.",
    highlights: ["Live work timer", "Explicit break log", "Session-based day", "Geo-aware capture"],
  },
  {
    index: "02",
    slug: "leaves",
    icon: CalendarDays,
    title: "Leave Management",
    tagline: "Policies encoded, not enforced by memory.",
    description:
      "Per-type leave templates: minimum notice, max duration, half-day, attachment rules, approval chains, colors and icons. Multi-approver routing with assigned HRs and project managers snapshotted at apply time.",
    highlights: ["Configurable templates", "Multi-approver chains", "Half-day support", "Attachment enforcement"],
  },
  {
    index: "03",
    slug: "projects",
    icon: KanbanSquare,
    title: "Projects & Kanban",
    tagline: "A board that mirrors how your team actually ships.",
    description:
      "Custom lanes per project, drag-and-drop cards, members with role-based access, task types (story / bug / task / spike), story points, attachments, comments with @mentions, and one-click reports.",
    highlights: ["Custom lanes per project", "Drag & drop", "@mention notifications", "Member management"],
  },
  {
    index: "04",
    slug: "sprints",
    icon: Rocket,
    title: "Sprints & Backlog",
    tagline: "Plan a two-week sprint in under two minutes.",
    description:
      "Groom a persistent backlog, spin up sprints with velocity forecasts, and burn work down with a live chart. Point up stories, drag them into a sprint, and let the report write itself.",
    highlights: ["Persistent backlog", "Sprint velocity", "Burndown chart", "Auto-generated reports"],
  },
  {
    index: "05",
    slug: "tasks",
    icon: ListChecks,
    title: "Global Tasks",
    tagline: "One workspace. Every task. Every filter.",
    description:
      "Cross-project task view with filters for project, assignee, status, priority, type, due window, and free-text search. Employee scoping is automatic — people only see what they should.",
    highlights: ["Cross-project view", "Powerful filters", "Overdue detection", "Role-scoped by default"],
  },
  {
    index: "06",
    slug: "timesheets",
    icon: Table2,
    title: "Timesheets",
    tagline: "Weekly entries. Per-project approval. CSV in one click.",
    description:
      "Weekly entry grid with per-day caps and project validation. Submit routes each project to its configured approver role — manager, HR, or a custom role you invent. Withdraw before anyone acts. Approve, reject with reason, or download the whole thing as CSV.",
    highlights: ["Weekly grid", "Per-project approver", "Withdraw & re-submit", "CSV export"],
  },
  {
    index: "07",
    slug: "expenses",
    icon: Receipt,
    title: "Expense Claims",
    tagline: "Receipts in. Reimbursements out.",
    description:
      "Submit claims with receipts, per-category limits, and multi-step approval. Approved expenses feed automatically into project budgets so you see real cost, not just planned cost.",
    highlights: ["Receipt upload", "Category limits", "Feeds into budgets", "Payment tracking"],
  },
  {
    index: "08",
    slug: "onboarding",
    icon: UserPlus,
    title: "Onboarding",
    tagline: "A first day the new hire remembers for the right reasons.",
    description:
      "Configurable checklists per role. Automatic task assignment to buddies, IT, and managers. Progress dashboard for HR. Onboarding done by design, not by luck.",
    highlights: ["Role-based checklists", "Buddy assignment", "HR progress view", "Template library"],
  },
  {
    index: "09",
    slug: "offboarding",
    icon: UserMinus,
    title: "Offboarding",
    tagline: "Exit gracefully. Recover everything.",
    description:
      "Structured exit workflows: asset return, access revocation, knowledge transfer, exit interviews. Nothing falls through the cracks when someone leaves.",
    highlights: ["Structured workflows", "Asset return", "Access revocation", "Exit interviews"],
  },
  {
    index: "10",
    slug: "jobs",
    icon: Briefcase,
    title: "Recruitment & Jobs",
    tagline: "From posting to offer letter — in one place.",
    description:
      "Publish job posts, collect applications, review candidates through a pipeline, and hand off to onboarding when they accept. Job templates keep language consistent across roles.",
    highlights: ["Public job posts", "Applicant pipeline", "Job templates", "Handoff to onboarding"],
  },
  {
    index: "11",
    slug: "referrals",
    icon: Gift,
    title: "Employee Referrals",
    tagline: "Your best recruiters already work for you.",
    description:
      "A first-class referral module: employees submit candidates, HR tracks status through the recruitment funnel, and bonuses are calculated by configurable policy.",
    highlights: ["Employee submissions", "Status tracking", "Bonus policy", "Recruiter analytics"],
  },
  {
    index: "12",
    slug: "issues",
    icon: Bug,
    title: "Issue Tracking",
    tagline: "A bug tracker that speaks the language of your team.",
    description:
      "Issue types, severity, reporter, assignee, linked project, comments, attachments, and full audit trail. Custom templates per issue type — because a P1 outage is not a UX polish.",
    highlights: ["Custom templates", "Severity levels", "Full audit trail", "Linked to projects"],
  },
  {
    index: "13",
    slug: "assets",
    icon: Laptop,
    title: "Asset Management",
    tagline: "Every laptop, every license — accounted for.",
    description:
      "Assign, return, retire. Track condition, serial numbers, warranties. Assets link to people so returns during offboarding are one click.",
    highlights: ["Assign & return", "Condition tracking", "Warranty & serials", "Offboarding aware"],
  },
  {
    index: "14",
    slug: "gamification",
    icon: Trophy,
    title: "Gamification",
    tagline: "A little dopamine goes a long way.",
    description:
      "Eleven built-in achievements auto-awarded on task completion and collaboration milestones. Leaderboards by week, month, or all-time. Champion callouts. Confetti when it counts.",
    highlights: ["11 achievements", "Leaderboards", "Auto-award engine", "Confetti moments"],
  },
  {
    index: "15",
    slug: "insights",
    icon: BarChart3,
    title: "Insights & Analytics",
    tagline: "Answers, not just charts.",
    description:
      "Personal dashboards with rank and points. Organization-wide analytics on people, projects, tasks, leaves, and attendance. Burndown, velocity, monthly champions — all live.",
    highlights: ["Personal dashboard", "Org-wide analytics", "Burndown charts", "Monthly champions"],
  },
  {
    index: "16",
    slug: "announcements",
    icon: Megaphone,
    title: "Announcements",
    tagline: "Broadcast that respects the audience.",
    description:
      "Target announcements by role, department, or project. Track reads. Never spam again — say what matters to the people it matters to.",
    highlights: ["Targeted delivery", "Read receipts", "Role filtering", "Rich formatting"],
  },
  {
    index: "17",
    slug: "idle-tracker",
    icon: Timer,
    title: "Idle Tracker",
    tagline: "Data-driven productivity — not surveillance.",
    description:
      "Optional client that flags long idle windows so people can push back on unrealistic schedules. Configurable, transparent, and off by default.",
    highlights: ["Configurable thresholds", "Transparent to user", "Opt-in per team", "Insight, not spyware"],
  },
  {
    index: "18",
    slug: "roles",
    icon: ShieldCheck,
    title: "Custom Roles & Permissions",
    tagline: "Design your own hierarchy. Not ours.",
    description:
      "Build any role your organization needs. Twenty+ capability flags cover every module. Multi-role users get the union of permissions. Guardrails keep admin lockout impossible.",
    highlights: ["Custom role builder", "20+ capabilities", "Multi-role users", "Lockout-proof admin"],
  },
  {
    index: "19",
    slug: "budget",
    icon: Wallet,
    title: "Project Budgets",
    tagline: "Labor + expenses + manual — in one number.",
    description:
      "Set a planned budget with role-based hourly rates. Approved timesheets become labor cost. Approved expenses become expense cost. Add manual lines for everything else. Alerts fire at 80% and 100%.",
    highlights: ["Role hourly rates", "Auto labor cost", "Threshold alerts", "Real-time consumption"],
  },
  {
    index: "20",
    slug: "branding",
    icon: Palette,
    title: "Branding",
    tagline: "Your workspace, your voice.",
    description:
      "Customize the login page, brand mark, colors, imagery, and copy per organization. Standalone module that lets your workspace feel like yours from the first pixel.",
    highlights: ["Custom login page", "Brand mark & colors", "Hero imagery", "Per-org overrides"],
  },
];

export const CONTACT = {
  emailSales: "hello@velacin.com",
  emailSupport: "support@velacin.com",
  phone: "+1 (555) 010-0100",
  hq: "Remote-first · Worldwide",
  linkedin: "https://www.linkedin.com/company/velacin",
  twitter: "https://twitter.com/velacin",
  github: "https://github.com/velacin",
};

export const SITE_URL = "https://velacin.com";
